import { useRef } from 'react'
import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faClock, faLock } from '@fortawesome/pro-solid-svg-icons'

import { Box, LoadingBubbles, Text } from '@domain-group/suit-theme'
import { useDraggable, useSetState } from '@domain-group/react-hooks'

import { getGridSize } from '@functions/orderItem'

import useArtwork from '@hooks/useArtwork'
import useArtworkPagePreview from '@hooks/useArtworkPagePreview'
import usePublicationBookingItem from '@hooks/usePublicationBookingItem'

import type { PublicationBookingItemModel } from '@models/types'

import Actions from './Actions'

import styles from './styles.module.scss'

type OnDragEndParams = {
  e: Event,
}

const onDragEnd = (params: OnDragEndParams) => {
  const { e: { target } } = params

  // @ts-expect-error TS18047
  target.style.webkitTransform = 'translate(0px, 0px)'
  // @ts-expect-error TS18047
  target.style.transform = 'translate(0px, 0px)'
  // @ts-expect-error TS18047
  target.setAttribute('data-x', 0)
  // @ts-expect-error TS18047
  target.setAttribute('data-y', 0)
}

type RenderStatusOverlayParams = {
  hasApprovedArtwork: boolean,
  isReleased: boolean,
  publicationBookingItem: PublicationBookingItemModel,
  buildingArtworkPreview: boolean,
}

const renderStatusOverlay = (params: RenderStatusOverlayParams) => {
  const {
    hasApprovedArtwork,
    isReleased,
    publicationBookingItem: item,
    buildingArtworkPreview,
  } = params

  if (buildingArtworkPreview){
    return (
      <Box className={classnames(styles.overlay, styles.loading)}>
        <LoadingBubbles color="white" />
      </Box>
    )
  }

  if (hasApprovedArtwork && !isReleased){
    return (
      <Box className={classnames(styles.overlay, styles.approved)}>
        <FontAwesomeIcon className={styles.icon} icon={faCheck} />
        <Text as="span" color="white">Approved</Text>
      </Box>
    )
  }

  if (isReleased){
    return (
      <Box className={classnames(styles.overlay, styles.approved)}>
        <FontAwesomeIcon className={styles.icon} icon={faLock} />
        <Text as="span" color="white">Released</Text>
      </Box>
    )
  }

  if (item.pending_cancellation){
    return (
      <Box className={classnames(styles.overlay, styles.cancelled)}>
        <FontAwesomeIcon className={styles.icon} icon={faClock} />
        <Text as="span" color="white">Cancelling</Text>
      </Box>
    )
  }

  return null
}

const defaultState = {
  approving: false,
  deleting: false,
  loadingCompatibleArtworks: false,
  renderingArtwork: false,
  unapproving: false,
}

interface BookingItemProps {
  approvedArtworkIds: number[],
  cellSize: {
    height: number,
    width: number,
  },
  isPending: boolean,
  isReleased: boolean,
  publicationBookingItem: PublicationBookingItemModel,
  zoom: number,
}

const BookingItem = (props: BookingItemProps) => {
  const {
    approvedArtworkIds,
    cellSize,
    isPending,
    isReleased,
    publicationBookingItem,
    zoom,
  } = props

  const [state, setState] = useSetState(defaultState)
  const {
    approving,
    deleting,
    loadingCompatibleArtworks,
    renderingArtwork,
    unapproving,
  } = state

  const publicationBookingItemPayload = usePublicationBookingItem(publicationBookingItem)
  const {
    agency,
    artwork,
    callbacks: {
      removeOrDeletePublicationBookingItem,
    },
    campaign,
    loading,
    orderItem,
    publicationBookingItem: {
      // @ts-expect-error TS2339
      grid,
      id,
      order_id,
      order_item_id,
      x,
      y,
    },
    serviceProduct,
  } = publicationBookingItemPayload

  const artworkPayload = useArtwork(
    artwork,
    {
      // @ts-expect-error TS2339
      performHttpRequests: !!artwork.cache_key,
      requestOptions: {
        include_artwork_pages: true,
        include_artwork_page_preview: true,
      },
    },
  )
  const {
    callbacks: {
      approveArtwork,
      unapproveArtwork,
    },
    filteredArtworkPages,
  } = artworkPayload

  // publication booking artworks only have 1 artwork page
  const selectedArtworkPage = filteredArtworkPages[0]

  const artworkPageOptions = { ablyPublicKey: process.env.REACT_APP_ABLY_ARTWORK_BUILDER_PUBLIC_KEY || '' }
  const artworkPagePreviewPayload = useArtworkPagePreview(artworkPageOptions)
  const { callbacks: { buildPreview }, buildingRef: { current: buildingArtworkPreview } } = artworkPagePreviewPayload

  // @ts-expect-error TS2339
  const hasApprovedArtwork = approvedArtworkIds?.includes(artwork.id)
  // @ts-expect-error TS2740
  const gridSize = getGridSize({ serviceProduct })

  const className = loading ? classnames(styles.root, styles.loading) : classnames(styles.root, 'draggable')

  const draggableRef = useRef(null)

  useDraggable(draggableRef, {
    callbacks: {
      // @ts-expect-error TS7006
      moveEnd: (_, e) => onDragEnd({ e }),
    },
    documentElementId: 'main-stage',
  })

  return (
    <Box
      alignItems="center"
      backgroundColor="rgba(2, 183, 226, 0.5)"
      backgroundSize="100% 100%"
      className={className}
      data-booking-item-id={id}
      // @ts-expect-error TS2339
      data-columns={gridSize.columns}
      data-item-type="publicationBookingItem"
      data-order-id={order_id}
      data-order-item-id={order_item_id}
      // @ts-expect-error TS2339
      data-rows={gridSize.rows}
      justifyContent="center"
      position="absolute"
      ref={draggableRef}
      zIndex={2}
      style={{
        // @ts-expect-error TS2322
        backgroundImage: artwork.preview ? `url(${artwork.preview.large})` : null,
        height: grid.rows * cellSize.height,
        // @ts-expect-error TS18048
        left: x * zoom,
        // @ts-expect-error TS18048
        top: y * zoom,
        width: grid.columns * cellSize.width,
      }}
    >
      {/* Actions */}
      <Actions
        // @ts-expect-error TS2740
        agency={agency}
        approving={approving}
        // @ts-expect-error TS2740
        artwork={artwork}
        callbacks={{
          approveArtwork: async () => {
            setState({ approving: true })
            await approveArtwork()
            setState({ approving: false })
          },
          buildPreview,
          removeOrDeletePublicationBookingItem: async () => {
            setState({ deleting: true })
            const result = await removeOrDeletePublicationBookingItem()
            setState({ deleting: false })
            return result
          },
          unapproveArtwork: async () => {
            setState({ unapproving: true })
            await unapproveArtwork()
            setState({ unapproving: false })
          },
        }}
        // @ts-expect-error TS2740
        campaign={campaign}
        className={styles.actions}
        deleting={deleting}
        hasApprovedArtwork={hasApprovedArtwork}
        isPending={isPending}
        isReleased={isReleased}
        // @ts-expect-error TS2740
        orderItem={orderItem}
        publicationBookingItem={publicationBookingItem}
        selectedArtworkPage={selectedArtworkPage}
        unapproving={unapproving}
      />
      {/* End Actions */}

      {/* Details */}
      {/*
       // @ts-expect-error TS2339 */}
      {!artwork.preview && !buildingArtworkPreview && (
        <Box
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
          textAlign="center"
          style={{ gap: 8 }}
        >
          {/*
           // @ts-expect-error TS2339 */}
          <Text color="white" fontSize="xxlarge" fontWeight="300">{campaign.address}</Text>
          {/*
           // @ts-expect-error TS2339 */}
          <Text color="white" fontSize="large">{campaign.suburb_name}</Text>
          {/*
           // @ts-expect-error TS2339 */}
          <Text color="white" fontSize="small">{agency.title}</Text>
          {/*
           // @ts-expect-error TS2339 */}
          <Text color="white" fontSize="small">{serviceProduct.title}</Text>
        </Box>
      )}
      {/* End Details */}

      {/* Overlays */}
      {renderStatusOverlay({
        hasApprovedArtwork,
        isReleased,
        publicationBookingItem,
        buildingArtworkPreview,
      })}
      {/* End Overlays */}
    </Box>
  )
}

export default BookingItem
